import React, { useContext, useState } from 'react';
import { useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import _get from 'lodash/get';
import useQuery from 'hooks/useQuery';
// Models
import { queListCarparks } from 'models/cpm/carpark';
// Components
import Button2 from 'components/buttons/Button2/Button2';
import Input from 'components/inputs/Input/Input';
import Dropdown from 'components/inputs/Dropdown/Dropdown';
import Checkbox from 'components/inputs/Checkbox/Checkbox';
// Icons
import { ReactComponent as MagnifyingGlassIcon } from 'assets/icons/magnifying-glass.svg';
import { ReactComponent as RefreshIcon } from 'assets/icons/refresh-action-bar.svg';
// Search Bar scope
import useDebounce from 'hooks/useDebounce';
import Status from '../Status/Status';
import { AccountsContext } from '../../contexts/AccountsContext';
import styles from './style.cssmodule.css';

const statuses = ['ACTIVE', /* 'INACTIVE',  'PENDING_EMAIL_VALIDATION', */ 'ARCHIVED'];

function SearchBar() {
  const { filters, dispatchFilters } = useContext(AccountsContext);
  const { formatMessage: t } = useIntl();
  const token = useSelector((state) => _get(state, 'auth.token', null));
  const slugId = useSelector((state) => _get(state, 'cpmSlug.current.result.id', null));

  // 🚘🚘🚘 Carpark
  const [filterCarpark, setFilterCarpark] = useState('');
  const DEBOUNCE_DELAY = 250;
  const filterCarparkDebounced = useDebounce(filterCarpark, DEBOUNCE_DELAY);

  const { /* loading: loadingCarparks, error, */ data } = useQuery({
    query: queListCarparks,
    variables: {
      input: {
        slugId,
      },
      ...(filterCarpark ? {
        search: {
          name: { ilike: filterCarparkDebounced },
        },
      } : null),
      sort: [
        { name: 'ASC' },
      ],
      pagination: {
        pageSize: 20,
      },
    },
    token,
    dependencies: [filterCarparkDebounced],
  });
  const carparks = _get(data, 'listCarparks.entries', []).map((carpark) => (typeof carpark.name === 'string' ? ({ ...carpark, name: carpark.name.replace(/^Parking\s/, '') }) : carpark));

  const ALL_CARPARKS = { id: 'all', name: t({ id: 'info.allCarparks' }) };
  let selectedCarpark = ALL_CARPARKS;
  if (_get(filters, 'carpark.values[0]', null)) {
    selectedCarpark = carparks.find((carpark) => carpark.name === filters.carpark.values[0]);
  }

  // 🟠 Status
  let selectedStatus = null;
  if (filters.status.values && filters.status.values.length >= 1) {
    const [firstStatus] = filters.status.values;
    selectedStatus = firstStatus;
  }

  return (
    <div className={styles.searchContainer}>
      {/* 🔍 Multisearch */}
      <div className={styles.multiSearchContainer}>
        <Input
          type="text"
          name="search"
          placeholder={t({ id: 'placeholder.searchByNameEmailPhoneAddressInvoice' })}
          inputIcon={<MagnifyingGlassIcon />}
          showCharactersCounter={false}
          maxLength={50}
          value={_get(filters, 'search.values[0]', null)}
          onChange={(e) => dispatchFilters({ type: 'SET_FILTER_VALUES', filter: 'search', values: [e.target.value] })}
          required
        />
      </div>
      {/* 🚘🚘🚘 Carpark */}
      <div className={styles.searchCarparkContainer} data-filter="carpark">
        <Dropdown
          name="carpark"
          inputIcon={<MagnifyingGlassIcon />}
          options={[ALL_CARPARKS, ...carparks]}
          selectedOption={selectedCarpark}
          canFilter
          filter={filterCarpark}
          filterOnChange={(e) => setFilterCarpark(_get(e, 'target.value', ''))}
          onChange={(newCarpark) => {
            // console.log(newCarpark);
            if (newCarpark.id === 'all') {
              dispatchFilters({ type: 'SET_FILTER_VALUES', filter: 'carpark', values: null });
              return;
            }

            let newValue = null;
            if ((filters.carpark.value && filters.carpark.value !== newCarpark.name) || !filters.carpark.value) newValue = newCarpark.name;
            dispatchFilters({ type: 'SET_FILTER_VALUES', filter: 'carpark', values: [newValue] });
          }}
          placeholder={t({ id: 'placeholder.carpark' })}
          renderOption={(option, onClick) => {
            const isSelectedOption = (filters.carpark.values && filters.carpark.values[0] === option.name) || (!filters.carpark.values && option.id === 'all');
            return (
              <div
                className={`${styles.carparkOption} ${isSelectedOption ? 'selected' : ''}`}
                onClick={() => onClick(option.id)}
              >
                {option.name}
              </div>
            );
          }}
          renderSelectedOption={() => (
            <div className={styles.selectedCarparkContainer}>
              {selectedCarpark.name}
            </div>
          )}
        />
      </div>
      {/* 🟠 Status */}
      <div className={styles.searchStatusContainer} data-filter="status">
        <Dropdown
          name="status"
          options={statuses}
          canSelectMultiple
          selectedOption={selectedStatus}
          handleClear={() => dispatchFilters({ type: 'SET_FILTER_VALUES', filter: 'status', values: null })}
          onChange={(newStatus) => {
            let newValues = null;
            if (filters.status.values && newStatus && !filters.status.values.includes(newStatus)) newValues = [...filters.status.values, newStatus];
            if (filters.status.values && newStatus && filters.status.values.includes(newStatus)) newValues = filters.status.values.filter((s) => s !== newStatus);
            if (!filters.status.values && newStatus) newValues = [newStatus];
            dispatchFilters({ type: 'SET_FILTER_VALUES', filter: 'status', values: newValues });
          }}
          placeholder={t({ id: 'placeholder.status' })}
          renderOption={(option, onClick) => {
            const isSelectedOption = filters.status.values && filters.status.values.includes(option);
            return (
              <div
                className={`${styles.statusOption} ${isSelectedOption ? 'selected' : ''}`}
                onClick={() => onClick(option)}
              >
                <Checkbox name={`checkbox_${option}`} checked={isSelectedOption} />
                <Status status={option} />
              </div>
            );
          }}
          renderSelectedOption={(option) => {
            if (!option) return null;
            return (
              <div className={styles.selectedStatusContainer}>
                <Status status={option} />
                {filters.status.values.length > 1 && <span>{`(+${filters.status.values.length - 1})`}</span>}
              </div>
            );
          }}
        />
      </div>
      {/* 🔄 Refresh icon */}
      <div className={styles.buttons}>
        <Button2
          key="reload"
          size="medium"
          type="primary"
          iconOnly
          onClick={() => dispatchFilters({ type: 'RESET_FILTERS' })}
        >
          <RefreshIcon />
        </Button2>
      </div>
    </div>
  );
}

export default SearchBar;



// WEBPACK FOOTER //
// ./routes/cpm/routes/accounts/components/SearchBar/SearchBar.jsx